import React, {createContext, useCallback, useContext, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'

// Lazy-load the drawer so it is excluded from the initial bundle
const AddToCartConfirmationDrawer = loadable(
    () => import('../components/add-to-cart-confirmation-drawer'),
    {ssr: false}
)

export const AddToCartConfirmationContext = createContext({
    isOpen: false,
    addedItem: null,
    openConfirmation: () => {},
    closeConfirmation: () => {},
    setConfirmationTrigger: () => {}
})

export const useAddToCartConfirmation = () => useContext(AddToCartConfirmationContext)

/**
 * Provides the add-to-cart confirmation drawer to the application.
 *
 * Consumers call `openConfirmation({ product, variant, quantity })` after a
 * successful add-to-cart to surface the drawer.
 */
export const AddToCartConfirmationProvider = ({children}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [addedItem, setAddedItem] = useState(null)
    // Stores the DOM element that triggered the quick-view so Chakra can
    // return focus to it (preserving scroll position) when the drawer closes.
    const triggerRef = useRef(null)

    const setConfirmationTrigger = useCallback((element) => {
        triggerRef.current = element || null
    }, [])

    const openConfirmation = useCallback(({product, variant, quantity}) => {
        setAddedItem({product, variant, quantity})
        setIsOpen(true)
    }, [])

    const closeConfirmation = useCallback(() => {
        setIsOpen(false)
    }, [])

    return (
        <AddToCartConfirmationContext.Provider
            value={{isOpen, addedItem, openConfirmation, closeConfirmation, setConfirmationTrigger}}
        >
            {children}
            <AddToCartConfirmationDrawer
                isOpen={isOpen}
                onClose={closeConfirmation}
                addedItem={addedItem}
                finalFocusRef={triggerRef}
            />
        </AddToCartConfirmationContext.Provider>
    )
}

AddToCartConfirmationProvider.propTypes = {
    children: PropTypes.node.isRequired
}
